import Phaser from 'phaser';
import { AABB, CollisionGrid } from '../engine/CollisionGrid';

export type EnemyType = 'slugger' | 'hopper' | 'shooter';

export enum EnemyState {
  IDLE,
  PATROL,
  CHASE,
  ATTACK,
  STUNNED,
  DEAD,
}

export interface EnemyConfig {
  type: EnemyType;
  x: number;
  y: number;
  patrolLeft?: number;
  patrolRight?: number;
}

/**
 * Base enemy class with common behavior
 */
export class Enemy {
  protected sprite: Phaser.GameObjects.Sprite | null = null;
  protected x: number;
  protected y: number;
  protected velocityX: number = 0;
  protected velocityY: number = 0;
  protected type: EnemyType;
  protected state: EnemyState = EnemyState.PATROL;
  protected facingRight: boolean = true;
  protected health: number = 1;
  protected dead: boolean = false;

  // Patrol bounds
  protected patrolLeft: number;
  protected patrolRight: number;

  // Physics
  protected speed: number = 30;
  protected gravity: number = 500;
  protected maxFallSpeed: number = 300;

  // Hitbox
  protected width: number = 14;
  protected height: number = 14;

  // Animation
  protected animFrame: number = 0;
  protected animTimer: number = 0;
  protected animSpeed: number = 200; // ms per frame

  protected collisionGrid: CollisionGrid | null = null;

  constructor(config: EnemyConfig) {
    this.x = config.x;
    this.y = config.y;
    this.type = config.type;
    this.patrolLeft = config.patrolLeft ?? config.x - 50;
    this.patrolRight = config.patrolRight ?? config.x + 50;

    // Type-specific settings
    switch (config.type) {
      case 'slugger':
        this.speed = 25;
        this.animSpeed = 300;
        break;
      case 'hopper':
        this.speed = 40;
        this.animSpeed = 400;
        break;
      case 'shooter':
        this.speed = 20;
        this.animSpeed = 500;
        break;
    }
  }

  init(scene: Phaser.Scene, collisionGrid: CollisionGrid): void {
    this.collisionGrid = collisionGrid;
    this.sprite = scene.add.sprite(this.x, this.y, this.type);
    this.sprite.setOrigin(0, 0);
  }

  fixedUpdate(dt: number, playerX: number, playerY: number): void {
    if (this.dead) return;

    const dtSec = dt / 1000;

    // Update animation
    this.animTimer += dt;
    if (this.animTimer >= this.animSpeed) {
      this.animTimer = 0;
      this.animFrame = (this.animFrame + 1) % 2;
      this.updateSprite();
    }

    // State machine
    switch (this.state) {
      case EnemyState.PATROL:
        this.updatePatrol(dtSec);
        break;
      case EnemyState.CHASE:
        this.updateChase(dtSec, playerX, playerY);
        break;
      case EnemyState.STUNNED:
        this.updateStunned(dtSec);
        break;
    }

    // Apply gravity
    this.velocityY += this.gravity * dtSec;
    this.velocityY = Math.min(this.velocityY, this.maxFallSpeed);

    // Apply velocity
    this.x += this.velocityX * dtSec;
    this.y += this.velocityY * dtSec;

    // Resolve collisions
    this.resolveCollisions();
  }

  protected updatePatrol(dtSec: number): void {
    // Move in patrol direction
    this.velocityX = this.facingRight ? this.speed : -this.speed;

    // Turn around at patrol bounds
    if (this.x <= this.patrolLeft) {
      this.facingRight = true;
    } else if (this.x >= this.patrolRight) {
      this.facingRight = false;
    }
  }

  protected updateChase(dtSec: number, playerX: number, playerY: number): void {
    // Move toward player
    const dx = playerX - this.x;
    this.facingRight = dx > 0;
    this.velocityX = this.facingRight ? this.speed * 1.5 : -this.speed * 1.5;
  }

  protected updateStunned(dtSec: number): void {
    this.velocityX *= 0.9; // Slow down
  }

  protected resolveCollisions(): void {
    if (!this.collisionGrid) return;

    const box: AABB = {
      x: this.x,
      y: this.y,
      width: this.width,
      height: this.height,
    };

    const result = this.collisionGrid.resolveCollision(box, this.velocityX, this.velocityY);

    if (result.collided) {
      this.x += result.pushX;
      this.y += result.pushY;

      if (result.onGround) {
        this.velocityY = 0;
      }

      if (result.onLeftWall || result.onRightWall) {
        this.facingRight = !this.facingRight;
        this.velocityX = 0;
      }
    }
  }

  protected updateSprite(): void {
    if (!this.sprite) return;

    // Update texture based on animation frame
    const textureName = this.animFrame === 0 ? this.type : `${this.type}2`;
    if (this.sprite.scene.textures.exists(textureName)) {
      this.sprite.setTexture(textureName);
    }

    // Flip based on direction
    this.sprite.setFlipX(!this.facingRight);
  }

  render(alpha: number): void {
    if (!this.sprite || this.dead) return;

    this.sprite.x = this.x;
    this.sprite.y = this.y;
  }

  getHitbox(): AABB {
    return {
      x: this.x,
      y: this.y,
      width: this.width,
      height: this.height,
    };
  }

  hit(): boolean {
    this.health--;
    if (this.health <= 0) {
      this.die();
      return true;
    }

    // Stun
    this.state = EnemyState.STUNNED;
    setTimeout(() => {
      if (!this.dead) {
        this.state = EnemyState.PATROL;
      }
    }, 500);

    return false;
  }

  die(): void {
    this.dead = true;
    if (this.sprite) {
      this.sprite.scene.tweens.add({
        targets: this.sprite,
        y: this.sprite.y + 10,
        alpha: 0,
        angle: 90,
        duration: 300,
        onComplete: () => {
          this.sprite?.destroy();
        },
      });
    }
  }

  isDead(): boolean {
    return this.dead;
  }

  getType(): EnemyType {
    return this.type;
  }

  setChasing(chasing: boolean): void {
    this.state = chasing ? EnemyState.CHASE : EnemyState.PATROL;
  }
}
